import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { DPFormField } from 'src/app/core/models/DPFormField';

/**
 * Checkbox component.
 *
 * Multiple checkboxes can be selected.
 *
 * The value can be:
 * - null
 * - JSON string of following object:
 *    {
 *      "selected option key 1": "selected option value 1",
 *      "selected option key 2": "selected option value 2",
 *      ...
 *    }
 */
@Component({
  selector: 'app-checkbox',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, ReactiveFormsModule],
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
})
export class CheckboxComponent implements OnInit {
  @Input() field: DPFormField;
  @Input() control: UntypedFormControl;
  @Input() disabled: boolean;

  public form: UntypedFormArray;
  private _unsubscribe$ = new Subject<void>();

  constructor(private formBuilder: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.createForm();
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  /**
   * Create form for option checkboxes and subfields.
   */
  private createForm() {
    this.form = this.formBuilder.array(
      this.field.options.map(() => this.formBuilder.control(false))
    );
    this.patchValue();
    this.form.valueChanges
      .pipe(
        takeUntil(this._unsubscribe$),
        map((values) => {
          /**
           * Format field value from checkbox options
           */
          const controlValue = this.field.options
            .filter((_, i) => values[i])
            .reduce(
              (prev, opt) => ({
                ...prev,
                [opt.key]: opt.value,
              }),
              {}
            );
          const strValue = JSON.stringify(controlValue);

          return this.control.value !== strValue ? strValue : null;
        }),
        filter((controlValue) => controlValue !== null)
      )
      .subscribe((controlValue) => {
        this.control.setValue(controlValue);
      });
  }

  /**
   * Set checkbox form value from control value.
   */
  private patchValue() {
    if (!this.field || !this.control) {
      return;
    }

    const options = JSON.parse(this.control.value || '{}');
    const keys = Object.keys(options);

    this.form.patchValue(
      this.field.options.map((opt) => keys.includes(opt.key.toString()))
    );
  }
}
